import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Button,
  Stack,
  Grid,
  Card,
  CardContent,
  InputAdornment,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  Link,
  IconButton,
  Avatar,
  Paper,
  Divider,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Search as SearchIcon,
  TrackChanges as TrackChangesIcon,
  Message as MessageIcon,
  HelpOutline as HelpOutlineIcon,
  Payment as PaymentIcon,
  AccountBalance as AccountBalanceIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  GridOnOutlined as GridOnOutlinedIcon,
  TrendingUp as TrendingUpIcon,
  AutoAwesome as AutoAwesomeIcon,
  Insights as InsightsIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Forum as ForumIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { DataGrid } from '@mui/x-data-grid';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { StatusBadge } from '../../components/common/StatusBadge';
import { useToast } from '../../hooks/useToast';
import { EmptyState } from '../../components/common/EmptyState';
import { historialEstrategiasApi } from '../../api/historialEstrategias';
import { mensajesClasificadosApi } from '../../api/mensajesClasificados';
import { loansUnderAlertApi } from '../../api/loansUnderAlert';
import { CollectionsImpactChart } from '../../components/analytics/CollectionsImpactChart';

/** Kobra IA — patrón de diseño Seguimiento / historial */
const K = {
  navy: '#121826',
  blue: '#4185F1',
  green: '#6BD56F',
  orange: '#FF9800',
  border: 'rgba(18, 24, 38, 0.08)',
  inputBg: '#EEF1F8',
  surface: '#F4F6F9',
  cardBg: '#FAFBFC',
};

const kobraFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 1.5,
    backgroundColor: K.inputBg,
    '& fieldset': { borderColor: 'rgba(18, 24, 38, 0.1)' },
    '&:hover fieldset': { borderColor: `${K.blue}55` },
    '&.Mui-focused fieldset': { borderColor: K.blue, borderWidth: 2 },
  },
};

const kobraDateFieldSx = {
  ...kobraFieldSx,
  '& .MuiOutlinedInput-root, & .MuiInputBase-root, & .MuiPickersInputBase-root': {
    ...kobraFieldSx['& .MuiOutlinedInput-root'],
    backgroundColor: '#fff !important',
  },
};

/** Estilos DataGrid historial — Kobra IA */
const historialDataGridSx = {
  border: `1px solid ${K.border}`,
  borderRadius: 2,
  overflow: 'hidden',
  bgcolor: '#fff',
  fontFamily: 'inherit',
  '--DataGrid-rowBorderColor': K.border,
  '& .MuiDataGrid-main': { borderRadius: 2 },
  '& .MuiDataGrid-columnHeaders': {
    borderBottom: `2px solid ${alpha(K.blue, 0.2)}`,
    bgcolor: K.cardBg,
    color: K.navy,
  },
  '& .MuiDataGrid-columnHeader': {
    py: 1.25,
    px: 1.5,
    outline: 'none !important',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 700,
    fontSize: '0.7rem',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: K.navy,
  },
  '& .MuiDataGrid-columnHeader .MuiDataGrid-sortIcon': {
    color: alpha(K.navy, 0.45),
  },
  '& .MuiDataGrid-columnHeader:hover .MuiDataGrid-sortIcon': {
    color: K.blue,
  },
  '& .MuiDataGrid-menuIconButton': {
    color: alpha(K.navy, 0.45),
  },
  '& .MuiDataGrid-menuIconButton:hover': {
    color: K.blue,
    bgcolor: alpha(K.blue, 0.06),
  },
  '& .MuiDataGrid-columnSeparator': {
    display: 'none',
  },
  '& .MuiDataGrid-cell': {
    borderBottom: `1px solid ${K.border}`,
    py: 1.75,
    px: 1.5,
    fontSize: '0.875rem',
    display: 'flex',
    alignItems: 'center',
    color: K.navy,
  },
  '& .MuiDataGrid-cell[data-field="id_prestamo"]': {
    overflow: 'visible',
  },
  '& .MuiDataGrid-row': {
    '&:nth-of-type(even)': {
      bgcolor: alpha(K.blue, 0.03),
    },
    '&:hover': {
      bgcolor: `${alpha(K.blue, 0.08)} !important`,
    },
  },
  '& .MuiDataGrid-footerContainer': {
    borderTop: `1px solid ${K.border}`,
    bgcolor: K.cardBg,
    minHeight: 52,
    '& .MuiTablePagination-root': { color: K.navy },
    '& .MuiTablePagination-selectIcon': { color: K.navy },
    '& .MuiIconButton-root': { color: K.blue },
  },
};

const kobraShell = {
  borderRadius: 2,
  boxShadow: '0 1px 4px rgba(18, 24, 38, 0.06)',
  border: `1px solid ${K.border}`,
  bgcolor: '#fff',
};

const kobraBtnPrimary = {
  textTransform: 'none',
  fontWeight: 700,
  borderRadius: 1.5,
  px: 2.5,
  '&&': {
    bgcolor: K.blue,
    color: '#fff',
    backgroundImage: 'none',
    border: 'none',
    boxShadow: `0 4px 14px ${alpha(K.blue, 0.35)}`,
    '&:hover': {
      bgcolor: '#3574d9',
      backgroundImage: 'none',
      border: 'none',
      boxShadow: `0 6px 18px ${alpha(K.blue, 0.38)}`,
    },
    '&:active': {
      border: 'none',
      boxShadow: `0 2px 10px ${alpha(K.blue, 0.35)}`,
    },
  },
};

const kobraFilterLabelSx = {
  fontWeight: 700,
  fontSize: '0.7rem',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: alpha(K.navy, 0.55),
  mb: 1,
  display: 'block',
};

/** Fondo de página Kobra (mockup transacciones / detalle) */
const kobraPageBg = '#F8FAFC';

const kobraNestedCard = {
  borderRadius: 2,
  border: `1px solid ${K.border}`,
  boxShadow: '0 1px 4px rgba(18, 24, 38, 0.06)',
  bgcolor: '#fff',
};

function kobraInitials(name) {
  if (!name || typeof name !== 'string') return '—';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

/** Primera fila por ID de préstamo; las filas sin préstamo se exportan todas. */
function dedupeHistorialByLoanId(records) {
  const seen = new Set();
  return records.filter((r) => {
    const raw = r.id_prestamo;
    const key =
      raw !== undefined && raw !== null && String(raw).trim() !== ''
        ? String(raw).trim().toLowerCase()
        : '';
    if (!key) return true;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

const PRESTAMOS_MOCKUP = [
  {
    id: 'PRE-000145',
    vecesContactadas: 7,
    respuestas: 4,
    promesasPago: 2,
    ultimaVezContactado: '2026-03-22T15:20:00',
    ultimaRespuesta: '2026-03-22T16:05:00',
    ultimaVezPagado: '2026-03-18',
    montoUltimoPago: 3500,
    estado: 'En mora temprana',
    scoreRiesgo: 'Medio',
    saldoPendiente: 48600,
    cuotaMensual: 5400,
    diasAtraso: 14,
    cliente: {
      nombre: 'Mariela Hernández',
      cedula: '402-1234567-8',
      telefono: '809-555-0123',
      correo: 'mariela.h@correo.com',
      direccion: 'Santo Domingo Este',
    },
    garante: {
      nombre: 'José A. Rodríguez',
      cedula: '001-5544332-1',
      telefono: '829-555-7788',
      parentesco: 'Hermano',
      direccion: 'Santo Domingo Norte',
    },
    promesas: [
      { fecha: '2026-03-19 10:15', compromiso: 'RD$ 2,500', estado: 'Cumplido' },
      { fecha: '2026-03-22 16:10', compromiso: 'RD$ 3,500', estado: 'Pendiente' },
    ],
    pagos: [
      { fecha: '2026-03-18', monto: 3500, canal: 'Transferencia' },
      { fecha: '2026-02-28', monto: 2800, canal: 'Caja' },
    ],
    contactos: [
      { fecha: '2026-03-22 16:05', canal: 'WhatsApp', resultado: 'Cliente respondió' },
      { fecha: '2026-03-22 15:20', canal: 'Llamada', resultado: 'Promesa de pago' },
      { fecha: '2026-03-20 11:02', canal: 'WhatsApp', resultado: 'Mensaje leído' },
    ],
    respuestasDetalle: [
      { fecha: '2026-03-22 16:05', texto: 'Cliente confirma que pagará el viernes.' },
      { fecha: '2026-03-20 11:02', texto: 'Indica que revisará su estado de cuenta.' },
    ],
    notasGestion: 'Cliente indica retraso por pago tardío de nómina. Recontactar 48h.',
  },
  {
    id: 'PRE-000233',
    vecesContactadas: 5,
    respuestas: 1,
    promesasPago: 1,
    ultimaVezContactado: '2026-03-21T10:45:00',
    ultimaRespuesta: '2026-03-20T09:30:00',
    ultimaVezPagado: '2026-03-10',
    montoUltimoPago: 2200,
    estado: 'En gestión activa',
    scoreRiesgo: 'Bajo',
    saldoPendiente: 23100,
    cuotaMensual: 4200,
    diasAtraso: 6,
    cliente: {
      nombre: 'Luis Mejía',
      cedula: '001-9988776-2',
      telefono: '829-333-4588',
      correo: 'lmejia@correo.com',
      direccion: 'Santiago',
    },
    garante: {
      nombre: 'Ana Mejía',
      cedula: '031-1122334-8',
      telefono: '809-777-2211',
      parentesco: 'Esposa',
      direccion: 'Santiago',
    },
    promesas: [{ fecha: '2026-03-20 09:40', compromiso: 'RD$ 2,000', estado: 'Pendiente' }],
    pagos: [{ fecha: '2026-03-10', monto: 2200, canal: 'Pago móvil' }],
    contactos: [
      { fecha: '2026-03-21 10:45', canal: 'WhatsApp', resultado: 'Sin respuesta' },
      { fecha: '2026-03-20 09:30', canal: 'WhatsApp', resultado: 'Cliente respondió' },
    ],
    respuestasDetalle: [
      { fecha: '2026-03-20 09:30', texto: 'Cliente solicita prórroga de 3 días para pagar.' },
    ],
    notasGestion: 'Perfil cooperativo. Confirmar fecha exacta de regularización.',
  },
  {
    id: 'PRE-000411',
    vecesContactadas: 9,
    respuestas: 6,
    promesasPago: 3,
    ultimaVezContactado: '2026-03-23T12:10:00',
    ultimaRespuesta: '2026-03-23T12:16:00',
    ultimaVezPagado: '2026-03-23',
    montoUltimoPago: 5000,
    estado: 'Recuperación positiva',
    scoreRiesgo: 'Bajo',
    saldoPendiente: 12400,
    cuotaMensual: 5000,
    diasAtraso: 0,
    cliente: {
      nombre: 'Rosa Castillo',
      cedula: '031-5554433-1',
      telefono: '849-111-2055',
      correo: 'rosa.castillo@correo.com',
      direccion: 'La Vega',
    },
    garante: {
      nombre: 'Miguel Castillo',
      cedula: '402-6677889-5',
      telefono: '849-500-1122',
      parentesco: 'Padre',
      direccion: 'La Vega',
    },
    promesas: [{ fecha: '2026-03-23 12:12', compromiso: 'RD$ 5,000', estado: 'Cumplido' }],
    pagos: [
      { fecha: '2026-03-23', monto: 5000, canal: 'Transferencia' },
      { fecha: '2026-03-05', monto: 4500, canal: 'Caja' },
    ],
    contactos: [
      { fecha: '2026-03-23 12:16', canal: 'WhatsApp', resultado: 'Cliente respondió y pagó' },
      { fecha: '2026-03-23 12:10', canal: 'Llamada', resultado: 'Recordatorio exitoso' },
    ],
    respuestasDetalle: [
      { fecha: '2026-03-23 12:16', texto: 'Envía comprobante de pago y confirma regularización.' },
    ],
    notasGestion: 'Cuenta casi al día. Mantener seguimiento preventivo semanal.',
  },
  {
    id: 'PRE-000527',
    vecesContactadas: 3,
    respuestas: 0,
    promesasPago: 0,
    ultimaVezContactado: '2026-03-19T08:40:00',
    ultimaRespuesta: null,
    ultimaVezPagado: null,
    montoUltimoPago: null,
    estado: 'Alto riesgo',
    scoreRiesgo: 'Alto',
    saldoPendiente: 68900,
    cuotaMensual: 6100,
    diasAtraso: 31,
    cliente: {
      nombre: 'Carlos Peña',
      cedula: '402-7788990-4',
      telefono: '809-987-6621',
      correo: 'cpena@correo.com',
      direccion: 'San Cristóbal',
    },
    garante: {
      nombre: 'María Díaz',
      cedula: '402-4433221-7',
      telefono: '809-333-9012',
      parentesco: 'Amiga',
      direccion: 'San Cristóbal',
    },
    promesas: [],
    pagos: [],
    contactos: [
      { fecha: '2026-03-19 08:40', canal: 'Llamada', resultado: 'No contesta' },
      { fecha: '2026-03-16 14:10', canal: 'WhatsApp', resultado: 'No leído' },
      { fecha: '2026-03-14 09:55', canal: 'WhatsApp', resultado: 'No respuesta' },
    ],
    respuestasDetalle: [],
    notasGestion: 'Escalar a canal alterno y verificar contactos secundarios.',
  },
];
const FORCE_PRESTAMOS_MOCKUP = true;

function PromesaCumplimientoSection({ clasificacionId, cumplimiento, updateMutation }) {
  const rawEstado = cumplimiento?.estado;
  const estado =
    typeof rawEstado === 'string' ? rawEstado.trim() : rawEstado || 'pendiente';
  const [notasLocal, setNotasLocal] = useState(cumplimiento?.notas ?? '');
  useEffect(() => {
    setNotasLocal(cumplimiento?.notas ?? '');
  }, [clasificacionId, cumplimiento?.notas]);

  const idStr = String(clasificacionId);
  const isSavingThis =
    updateMutation.isPending &&
    updateMutation.variables &&
    String(updateMutation.variables.clasificacionId) === idStr;

  const estLabel =
    estado === 'cumplido' ? 'Cumplido' : estado === 'incumplido' ? 'Incumplido' : 'Pendiente';
  const chipColor =
    estado === 'cumplido' ? 'success' : estado === 'incumplido' ? 'error' : 'warning';

  const setEstado = (next) => {
    if (next === estado) return;
    updateMutation.mutate({ clasificacionId: idStr, payload: { estado: next } });
  };

  const serverNotas = cumplimiento?.notas ?? '';
  const notasDirty = (notasLocal || '') !== (serverNotas || '');

  const guardarNota = () => {
    if (!notasDirty) return;
    updateMutation.mutate({
      clasificacionId: idStr,
      payload: { estado, notas: notasLocal.trim() ? notasLocal : null },
    });
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        Cumplimiento de la promesa
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1.5 }}>
        <Chip label={estLabel} color={chipColor} size="small" sx={{ fontWeight: 600 }} />
      </Box>
      <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
        <Button
          size="small"
          variant={estado === 'pendiente' ? 'contained' : 'outlined'}
          disabled={isSavingThis}
          onClick={() => setEstado('pendiente')}
        >
          Pendiente
        </Button>
        <Button
          size="small"
          variant={estado === 'cumplido' ? 'contained' : 'outlined'}
          color="success"
          disabled={isSavingThis}
          onClick={() => setEstado('cumplido')}
        >
          Cumplido
        </Button>
        <Button
          size="small"
          variant={estado === 'incumplido' ? 'contained' : 'outlined'}
          color="error"
          disabled={isSavingThis}
          onClick={() => setEstado('incumplido')}
        >
          Incumplido
        </Button>
      </Stack>
      <Stack spacing={1} sx={{ alignItems: 'flex-start' }}>
        <TextField
          label="Notas (opcional)"
          multiline
          minRows={2}
          size="small"
          fullWidth
          value={notasLocal}
          disabled={isSavingThis}
          onChange={(e) => setNotasLocal(e.target.value)}
        />
        <Button
          size="small"
          variant="outlined"
          onClick={guardarNota}
          disabled={isSavingThis || !notasDirty}
        >
          Guardar nota
        </Button>
      </Stack>
      {cumplimiento?.actualizado_en && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Actualizado {dayjs(cumplimiento.actualizado_en).format('DD/MM/YYYY HH:mm')}
          {cumplimiento.actualizado_por?.nombre
            ? ` · ${cumplimiento.actualizado_por.nombre}`
            : ''}
        </Typography>
      )}
    </Box>
  );
}

export const Seguimiento = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [fechaInicio, setFechaInicio] = useState(null);
  const [fechaFin, setFechaFin] = useState(null);
  
  // Filters for mensajes clasificados
  const [tipoClasificacionFilter, setTipoClasificacionFilter] = useState('all');
  const [fechaInicioClasificados, setFechaInicioClasificados] = useState(null);
  const [fechaFinClasificados, setFechaFinClasificados] = useState(null);
  const [searchTermClasificados, setSearchTermClasificados] = useState('');
  /** null = el usuario aún no ha ejecutado una búsqueda; luego snapshot de filtros aplicados */
  const [clasificadosFiltrosAplicados, setClasificadosFiltrosAplicados] = useState(null);
  const [clasificadosBusquedaRev, setClasificadosBusquedaRev] = useState(0);
  const [loansAlertExpanded, setLoansAlertExpanded] = useState(true);
  const [selectedPrestamoId, setSelectedPrestamoId] = useState('');
  const [timelineFilter, setTimelineFilter] = useState('all');
  const [notasPrestamo, setNotasPrestamo] = useState({});
  const [notaPrestamoInput, setNotaPrestamoInput] = useState('');

  const { showToast, ToastComponent } = useToast();
  const queryClient = useQueryClient();

  const updateCumplimientoMutation = useMutation({
    mutationFn: ({ clasificacionId, payload }) =>
      mensajesClasificadosApi.updateCumplimientoPromesa(clasificacionId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mensajesClasificados'] });
      queryClient.invalidateQueries({ queryKey: ['mensajesClasificadosPromesasHoy'] });
    },
    onError: (err) => {
      showToast(err.message || 'Error al actualizar cumplimiento', 'error');
    },
  });

  const { data: seguimientoData, isLoading, error } = useQuery({
    queryKey: ['historialEstrategias'],
    queryFn: () => historialEstrategiasApi.getHistorialEstrategias(),
  });

  // Query for loans under alert (overdue installments) - only when Métricas tab is active
  const { data: loansUnderAlertData, isLoading: isLoadingLoansAlert, error: errorLoansAlert } = useQuery({
    queryKey: ['loansUnderAlert'],
    queryFn: () => loansUnderAlertApi.getLoansUnderAlert(),
    enabled: activeTab === 3,
  });

  const {
    data: mensajesPrestamosData,
    isLoading: isLoadingMensajesPrestamos,
    error: errorMensajesPrestamos,
  } = useQuery({
    queryKey: ['mensajesClasificadosPrestamos'],
    queryFn: () => mensajesClasificadosApi.getMensajesClasificados({ limit: 500 }),
    enabled: activeTab === 2,
  });

  const hoyPromesasStr = dayjs().format('YYYY-MM-DD');
  const {
    data: promesasHoyData,
    isLoading: isLoadingPromesasHoy,
    error: errorPromesasHoy,
  } = useQuery({
    queryKey: ['mensajesClasificadosPromesasHoy', hoyPromesasStr],
    queryFn: () =>
      mensajesClasificadosApi.getMensajesClasificados({
        tipo_clasificacion: 'promesa_pago',
        fecha_promesa_hoy: true,
        solo_cumplimiento_pendiente: true,
        limit: 100,
      }),
    enabled: activeTab === 1,
  });

  const ejecutarBusquedaClasificados = () => {
    const tieneTipo = tipoClasificacionFilter !== 'all';
    const tieneFechas = Boolean(fechaInicioClasificados || fechaFinClasificados);
    if (!tieneTipo && !tieneFechas) {
      showToast('Elige al menos un filtro: tipo de clasificación y/o rango de fechas.', 'warning');
      return;
    }
    setClasificadosFiltrosAplicados({
      tipo: tipoClasificacionFilter,
      fechaInicio: fechaInicioClasificados,
      fechaFin: fechaFinClasificados,
    });
    setClasificadosBusquedaRev((r) => r + 1);
  };

  // Query for mensajes clasificados: solo tras pulsar Buscar con filtros aplicados
  const { data: mensajesClasificadosData, isLoading: isLoadingClasificados, error: errorClasificados } = useQuery({
    queryKey: [
      'mensajesClasificados',
      clasificadosFiltrosAplicados?.tipo,
      clasificadosFiltrosAplicados?.fechaInicio?.format?.('YYYY-MM-DD') ?? null,
      clasificadosFiltrosAplicados?.fechaFin?.format?.('YYYY-MM-DD') ?? null,
      clasificadosBusquedaRev,
    ],
    queryFn: () => {
      const a = clasificadosFiltrosAplicados;
      const filters = {};
      if (a.tipo !== 'all') {
        filters.tipo_clasificacion = a.tipo;
      }
      if (a.fechaInicio) {
        filters.fecha_desde = a.fechaInicio.format('YYYY-MM-DD');
      }
      if (a.fechaFin) {
        filters.fecha_hasta = a.fechaFin.format('YYYY-MM-DD');
      }
      return mensajesClasificadosApi.getMensajesClasificados(filters);
    },
    enabled: activeTab === 1 && clasificadosFiltrosAplicados != null,
  });

  // Normalize estado values from English to Spanish
  const normalizeEstado = (estado) => {
    if (!estado) return 'pendiente';
    const estadoLower = estado.toLowerCase();
    const estadoMap = {
      'failed': 'fallido',
      'read': 'leido',
      'delivered': 'entregado',
      'sent': 'enviado',
      'completed': 'completada',
      'pending': 'pendiente',
    };
    return estadoMap[estadoLower] || estadoLower;
  };

  // Handle errors
  useEffect(() => {
    if (error) {
      showToast(error.message || 'Error al cargar historial de estrategias', 'error');
    }
    if (errorClasificados) {
      showToast(errorClasificados.message || 'Error al cargar mensajes clasificados', 'error');
    }
    if (errorLoansAlert) {
      showToast(errorLoansAlert.message || 'Error al cargar préstamos en alerta', 'error');
    }
    if (errorMensajesPrestamos) {
      showToast(errorMensajesPrestamos.message || 'Error al cargar resumen de préstamos', 'error');
    }
    if (errorPromesasHoy) {
      showToast(errorPromesasHoy.message || 'Error al cargar promesas para hoy', 'error');
    }
  }, [error, errorClasificados, errorLoansAlert, errorMensajesPrestamos, errorPromesasHoy, showToast]);

  // Fix body overflow to allow scrolling
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'auto';
    
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  // Filter and search seguimiento records
  const filteredSeguimiento = useMemo(() => {
    if (!seguimientoData) return [];

    let filtered = [...seguimientoData];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(record => 
        (record.id_prestamo && record.id_prestamo.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (record.nombre_estrategia && record.nombre_estrategia.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Platform filter
    if (platformFilter !== 'all') {
      filtered = filtered.filter(record => record.plataforma === platformFilter);
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(record => {
        const normalizedEstado = normalizeEstado(record.estado);
        return normalizedEstado === statusFilter;
      });
    }

    // Date range filter - fechaInicio
    if (fechaInicio) {
      filtered = filtered.filter(record => {
        if (!record.created_at) return false;
        try {
          const rowDate = dayjs(record.created_at, 'DD/MM/YYYY');
          return rowDate.isAfter(fechaInicio, 'day') || rowDate.isSame(fechaInicio, 'day');
        } catch (e) {
          console.error('Error parsing date:', e, record.created_at);
          return false;
        }
      });
    }

    // Date range filter - fechaFin
    if (fechaFin) {
      filtered = filtered.filter(record => {
        if (!record.created_at) return false;
        try {
          const rowDate = dayjs(record.created_at, 'DD/MM/YYYY');
          return rowDate.isBefore(fechaFin, 'day') || rowDate.isSame(fechaFin, 'day');
        } catch (e) {
          console.error('Error parsing date:', e, record.created_at);
          return false;
        }
      });
    }

    return filtered;
  }, [seguimientoData, searchTerm, platformFilter, statusFilter, fechaInicio, fechaFin]);

  const seguimientoKpis = useMemo(() => {
    const rows = filteredSeguimiento;
    const n = rows.length;
    if (n === 0) {
      return { total: 0, openRatePct: 0, successRatePct: 0 };
    }
    const norm = (estado) => {
      if (!estado) return 'pendiente';
      const el = String(estado).toLowerCase();
      const map = {
        failed: 'fallido',
        read: 'leido',
        delivered: 'entregado',
        sent: 'enviado',
        completed: 'completada',
        pending: 'pendiente',
      };
      return map[el] || el;
    };
    const readCount = rows.filter((r) => norm(r.estado) === 'leido').length;
    const successCount = rows.filter((r) => {
      const e = norm(r.estado);
      return e === 'completada' || e === 'leido' || e === 'entregado';
    }).length;
    return {
      total: n,
      openRatePct: Math.round((readCount / n) * 1000) / 10,
      successRatePct: Math.round((successCount / n) * 1000) / 10,
    };
  }, [filteredSeguimiento]);

  const clearHistorialFilters = () => {
    setSearchTerm('');
    setPlatformFilter('all');
    setStatusFilter('all');
    setFechaInicio(null);
    setFechaFin(null);
  };

  // Filter mensajes clasificados
  const filteredMensajesClasificados = useMemo(() => {
    if (!mensajesClasificadosData?.mensajes) return [];

    let filtered = [...mensajesClasificadosData.mensajes];

    // Search filter
    if (searchTermClasificados) {
      filtered = filtered.filter(mensaje => 
        (mensaje.mensaje_texto && mensaje.mensaje_texto.toLowerCase().includes(searchTermClasificados.toLowerCase())) ||
        (mensaje.transmisor && mensaje.transmisor.includes(searchTermClasificados)) ||
        (mensaje.cliente?.nombre && mensaje.cliente.nombre.toLowerCase().includes(searchTermClasificados.toLowerCase())) ||
        (mensaje.cliente?.cedula && mensaje.cliente.cedula.includes(searchTermClasificados))
      );
    }

    return filtered;
  }, [mensajesClasificadosData, searchTermClasificados]);

  const prestamosResumen = useMemo(() => {
    const porPrestamo = new Map();
    const ensureLoan = (loanId) => {
      const key = String(loanId || '').trim();
      if (!key) return null;
      if (!porPrestamo.has(key)) {
        porPrestamo.set(key, {
          id: key,
          vecesContactadas: 0,
          respuestas: 0,
          promesasPago: 0,
          ultimaVezContactado: null,
          ultimaRespuesta: null,
          ultimaVezPagado: null,
          montoUltimoPago: null,
        });
      }
      return porPrestamo.get(key);
    };

    (seguimientoData || []).forEach((record) => {
      const loan = ensureLoan(record?.id_prestamo);
      if (!loan) return;
      loan.vecesContactadas += 1;
      if (record?.created_at) {
        const fechaContacto = dayjs(record.created_at, 'DD/MM/YYYY HH:mm');
        if (fechaContacto.isValid()) {
          if (!loan.ultimaVezContactado || fechaContacto.isAfter(loan.ultimaVezContactado)) {
            loan.ultimaVezContactado = fechaContacto;
          }
        }
      }
    });

    (mensajesPrestamosData?.mensajes || []).forEach((mensaje) => {
      const loanId = mensaje?.clasificacion?.ultimo_pago?.loan_id;
      const loan = ensureLoan(loanId);
      if (!loan) return;

      loan.respuestas += 1;
      const fechaMensaje = mensaje?.fecha ? dayjs(mensaje.fecha) : null;
      if (fechaMensaje?.isValid()) {
        if (!loan.ultimaRespuesta || fechaMensaje.isAfter(loan.ultimaRespuesta)) {
          loan.ultimaRespuesta = fechaMensaje;
        }
      }

      const tipo = mensaje?.clasificacion?.tipo;
      if (tipo === 'promesa_pago') {
        loan.promesasPago += 1;
      }

      const ultimoPagoFechaRaw = mensaje?.clasificacion?.ultimo_pago?.fecha_ultimo_pago;
      const ultimoPagoMontoRaw = mensaje?.clasificacion?.ultimo_pago?.monto_ultimo_pago;
      const ultimoPagoFecha = ultimoPagoFechaRaw ? dayjs(ultimoPagoFechaRaw) : null;
      if (ultimoPagoFecha?.isValid()) {
        if (!loan.ultimaVezPagado || ultimoPagoFecha.isAfter(loan.ultimaVezPagado)) {
          loan.ultimaVezPagado = ultimoPagoFecha;
          loan.montoUltimoPago =
            typeof ultimoPagoMontoRaw === 'number' ? ultimoPagoMontoRaw : Number(ultimoPagoMontoRaw) || null;
        }
      }
    });

    return Array.from(porPrestamo.values()).sort((a, b) => {
      const aFecha = a.ultimaVezContactado ? a.ultimaVezContactado.valueOf() : 0;
      const bFecha = b.ultimaVezContactado ? b.ultimaVezContactado.valueOf() : 0;
      return bFecha - aFecha;
    });
  }, [seguimientoData, mensajesPrestamosData]);

  const prestamosResumenData = useMemo(() => {
    if (FORCE_PRESTAMOS_MOCKUP) {
      return PRESTAMOS_MOCKUP.map((item) => ({
        ...item,
        ultimaVezContactado: item.ultimaVezContactado ? dayjs(item.ultimaVezContactado) : null,
        ultimaRespuesta: item.ultimaRespuesta ? dayjs(item.ultimaRespuesta) : null,
        ultimaVezPagado: item.ultimaVezPagado ? dayjs(item.ultimaVezPagado) : null,
      }));
    }
    if (prestamosResumen.length > 0) return prestamosResumen;
    return PRESTAMOS_MOCKUP.map((item) => ({
      ...item,
      ultimaVezContactado: item.ultimaVezContactado ? dayjs(item.ultimaVezContactado) : null,
      ultimaRespuesta: item.ultimaRespuesta ? dayjs(item.ultimaRespuesta) : null,
      ultimaVezPagado: item.ultimaVezPagado ? dayjs(item.ultimaVezPagado) : null,
    }));
  }, [prestamosResumen]);

  const prestamosExpedienteData = useMemo(() => {
    return prestamosResumenData.map((loan) => ({
      ...loan,
      estado: loan.estado || 'En gestión',
      scoreRiesgo: loan.scoreRiesgo || 'Medio',
      saldoPendiente: loan.saldoPendiente ?? null,
      cuotaMensual: loan.cuotaMensual ?? null,
      diasAtraso: loan.diasAtraso ?? null,
      cliente: loan.cliente || {
        nombre: 'Cliente no identificado',
        cedula: '—',
        telefono: '—',
        correo: '—',
        direccion: '—',
      },
      garante: loan.garante || {
        nombre: 'Sin garante registrado',
        cedula: '—',
        telefono: '—',
        parentesco: '—',
        direccion: '—',
      },
      promesas: Array.isArray(loan.promesas) ? loan.promesas : [],
      pagos: Array.isArray(loan.pagos) ? loan.pagos : [],
      contactos: Array.isArray(loan.contactos) ? loan.contactos : [],
      respuestasDetalle: Array.isArray(loan.respuestasDetalle)
        ? loan.respuestasDetalle
        : (Array.isArray(loan.contactos) ? loan.contactos : [])
            .filter((c) => {
              const resultado = String(c?.resultado || '').toLowerCase();
              return resultado.includes('respond') || resultado.includes('contesta') || resultado.includes('confirm');
            })
            .map((c) => ({
              fecha: c?.fecha || '—',
              texto: c?.resultado || 'Respuesta registrada',
            })),
      notasGestion: loan.notasGestion || 'Sin notas registradas.',
    }));
  }, [prestamosResumenData]);

  useEffect(() => {
    if (!prestamosExpedienteData.length) {
      setSelectedPrestamoId('');
      return;
    }
    const exists = prestamosExpedienteData.some((loan) => loan.id === selectedPrestamoId);
    if (!exists) {
      setSelectedPrestamoId(prestamosExpedienteData[0].id);
    }
  }, [prestamosExpedienteData, selectedPrestamoId]);

  const selectedPrestamo = useMemo(() => {
    return (
      prestamosExpedienteData.find((loan) => loan.id === selectedPrestamoId) ||
      prestamosExpedienteData[0] ||
      null
    );
  }, [prestamosExpedienteData, selectedPrestamoId]);

  useEffect(() => {
    if (!selectedPrestamo) {
      setNotaPrestamoInput('');
      return;
    }
    setNotaPrestamoInput('');
  }, [selectedPrestamo]);

  const columns = [
    {
      field: 'nombre_estrategia',
      headerName: 'Campaña',
      minWidth: 150,
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ py: 0.25 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              color: K.navy,
              lineHeight: 1.35,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {params.value || '—'}
          </Typography>
         
        </Box>
      ),
    },
    {
      field: 'id_prestamo',
      headerName: 'Destinatario',
      width: 220,
      minWidth: 200,
      flex: 0,
      sortable: false,
      valueGetter: (value, row) => {
        const prestamo =
          row?.id_prestamo != null && String(row.id_prestamo).trim() !== '' ? String(row.id_prestamo).trim() : '';
        const mensaje =
          row?.id_mensaje != null && String(row.id_mensaje).trim() !== '' ? String(row.id_mensaje).trim() : '';
        return prestamo || mensaje || '';
      },
      renderCell: (params) => {
        const row = params.row;
        const prestamo =
          row?.id_prestamo != null && String(row.id_prestamo).trim() !== '' ? String(row.id_prestamo).trim() : '';
        const mensaje =
          row?.id_mensaje != null && String(row.id_mensaje).trim() !== '' ? String(row.id_mensaje).trim() : '';
        const primary = prestamo || mensaje;
        const extraLine = prestamo && mensaje && mensaje !== prestamo ? mensaje : '';
        const initials = primary
          ? (primary.replace(/[^a-zA-Z0-9]/g, '').slice(-2) || 'ID').toUpperCase().slice(0, 2)
          : '?';
        return (
          <Box
            sx={{
              width: '100%',
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              py: 0.25,
            }}
          >
            <Avatar
              sx={{
                width: 80,
                height: 40,
                fontSize: '0.8rem',
                fontWeight: 800,
                flexShrink: 0,
                bgcolor: alpha(K.blue, 0.14),
                color: K.blue,
                border: `1px solid ${alpha(K.blue, 0.22)}`,
              }}
            >
              {primary}
            </Avatar>
{/*            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 800,
                  color: alpha(K.navy, 0.45),
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  fontSize: '0.65rem',
                  display: 'block',
                  mb: 0.25,
                }}
              >
                {prestamo ? 'Préstamo' : mensaje ? 'Referencia' : '—'}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: K.navy,
                  lineHeight: 1.25,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={primary || undefined}
              >
                {primary || 'Sin identificador'}
              </Typography>
              {extraLine ? (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    display: 'block',
                    mt: 0.25,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: '0.7rem',
                  }}
                  title={extraLine}
                >
                  Msg · {extraLine}
                </Typography>
              ) : null}
            </Box>
*/}          </Box>
        );
      },
    },
    {
      field: 'plataforma',
      headerName: 'Canal',
      minWidth: 140,
      flex: 0,
      width: 150,
      renderCell: (params) => {
        const p = (params.value || '').toLowerCase();
        const dot =
          p === 'whatsapp'
            ? K.green
            : p === 'email'
            ? K.orange
            : p === 'llamada'
            ? K.blue
            : alpha(K.navy, 0.2);
        const label = params.value || '—';
        return (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              height: 30,
              px: 1.25,
              borderRadius: 1.5,
              bgcolor: alpha(dot, 0.1),
              border: `1px solid ${alpha(dot, 0.32)}`,
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: dot,
                flexShrink: 0,
              }}
            />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                fontSize: '0.8125rem',
                textTransform: 'capitalize',
                color: p === 'whatsapp' ? '#1B5E20' : p === 'email' ? '#E65100' : K.navy,
              }}
            >
              {label}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: 'created_at',
      headerName: 'Fecha / hora',
      minWidth: 150,
      flex: 0,
      width: 160,
      renderCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: 0.5,
            px: 1,
            borderRadius: 1.5,
            bgcolor: alpha(K.navy, 0.04),
            border: `1px solid ${alpha(K.navy, 0.06)}`,
            width: '100%',
            maxWidth: '100%',
          }}
        >
          <CalendarIcon sx={{ fontSize: 18, color: alpha(K.navy, 0.4), flexShrink: 0 }} />
          <Typography
            variant="body2"
            sx={{
              color: K.navy,
              fontWeight: 600,
              fontVariantNumeric: 'tabular-nums',
              fontSize: '0.8125rem',
            }}
          >
            {params.value ?? '—'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'estado',
      headerName: 'Estado',
      minWidth: 140,
      flex: 0,
      width: 150,
      renderCell: (params) => {
        const normalizedStatus = normalizeEstado(params.value);
        return (
          <Box sx={{ py: 0.25 }}>
            <StatusBadge status={normalizedStatus} size="small" />
          </Box>
        );
      },
    },
    {
      field: '_actions',
      headerName: 'Acciones',
      width: 96,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      renderCell: () => (
        <IconButton
          size="small"
          aria-label="Detalle"
          sx={{
            color: K.blue,
            bgcolor: alpha(K.blue, 0.08),
            border: `1px solid ${alpha(K.blue, 0.2)}`,
            borderRadius: 1.5,
            '&:hover': { bgcolor: alpha(K.blue, 0.16) },
          }}
        >
          <InfoIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  const handleExportHistorialExcel = () => {
    if (filteredSeguimiento.length === 0) {
      showToast('No hay datos para exportar', 'warning');
      return;
    }
    try {
      const rows = dedupeHistorialByLoanId(filteredSeguimiento);
      const sheetRows = rows.map((r) => ({
        ID: r.id,
        Estrategia: r.nombre_estrategia ?? '',
        'ID Préstamo': r.id_prestamo ?? '',
        Plataforma: r.plataforma ?? '',
        Estado: normalizeEstado(r.estado),
        Fecha: r.created_at ?? '',
      }));
      const worksheet = XLSX.utils.json_to_sheet(sheetRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Historial estrategias');
      const filename = `historial_estrategias_${dayjs().format('YYYY-MM-DD_HH-mm')}.xlsx`;
      XLSX.writeFile(workbook, filename);
    } catch (err) {
      console.error(err);
      showToast(err.message || 'Error al exportar a Excel', 'error');
    }
  };

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3 },
        width: '100%',
        maxWidth: { sm: '100%', md: '1700px' },
        display: 'flex',
        flexDirection: 'column',
        alignSelf: 'stretch',
      }}
    >
      <ToastComponent />

      {/* Cabecera estilo Kobra */}
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { md: 'flex-start' },
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <Box
            sx={{
              p: 1.25,
              borderRadius: 1.5,
              backgroundColor: K.blue,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <TrackChangesIcon sx={{ fontSize: 28, color: 'white' }} />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: K.navy, letterSpacing: '-0.02em', mb: 0.5 }}>
              Historial de mensajes
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(18, 24, 38, 0.55)', maxWidth: 560 }}>
              Seguimiento de envíos, canales y estados de entrega. Gestiona campañas y recuperación desde un solo
              panel.
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ flexShrink: 0 }}>
          <Button
            variant="outlined"
            size="medium"
            startIcon={<PictureAsPdfIcon />}
            onClick={() => showToast('Exportar PDF disponible próximamente', 'info')}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 1.5,
              borderColor: K.border,
              color: K.navy,
            }}
          >
            Exportar PDF
          </Button>
          <Button
            variant="contained"
            size="medium"
            startIcon={<GridOnOutlinedIcon />}
            onClick={handleExportHistorialExcel}
            sx={kobraBtnPrimary}
          >
            Exportar Excel
          </Button>
        </Stack>
      </Box>

      {/* Tabs */}
      <Box
        sx={{
          borderBottom: 1,
          borderColor: K.border,
          mb: 2,
          flexShrink: 0,
          '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, color: 'rgba(18,24,38,0.5)' },
          '& .MuiTab-root.Mui-selected': { color: K.blue },
          '& .MuiTabs-indicator': { backgroundColor: K.blue, height: 3, borderRadius: 1 },
        }}
      >
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} aria-label="seguimiento tabs">
          <Tab
            label={`Historial de Estrategias${filteredSeguimiento.length > 0 ? ` (${filteredSeguimiento.length})` : ''}`}
          />
          <Tab
            label={`Mensajes Clasificados${
              clasificadosFiltrosAplicados != null && mensajesClasificadosData?.total != null
                ? ` (${mensajesClasificadosData.total})`
                : ''
            }`}
          />
          <Tab
            label={`Préstamos${prestamosExpedienteData.length > 0 ? ` (${prestamosExpedienteData.length})` : ''}`}
          />
          <Tab label="Métricas" />
        </Tabs>
      </Box>

      {/* Tab Historial: búsqueda global + KPIs + barra de filtros */}
      {activeTab === 0 && (
        <>
          <TextField
            fullWidth
            placeholder="Buscar mensajes, clientes o campañas…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'rgba(18,24,38,0.38)' }} />
                </InputAdornment>
              ),
            }}
            sx={{ ...kobraFieldSx, mb: 2 }}
          />

          {!isLoading && (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: `1px solid ${K.border}`,
                    borderLeft: `4px solid ${K.blue}`,
                    bgcolor: '#fff',
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 1.5,
                        bgcolor: alpha(K.blue, 0.12),
                        color: K.blue,
                        display: 'flex',
                      }}
                    >
                      <MessageIcon />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        Total de mensajes (vista filtrada)
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: K.navy, my: 0.5 }}>
                        {seguimientoKpis.total.toLocaleString('es-DO')}
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <TrendingUpIcon sx={{ fontSize: 16, color: K.green }} />
                        <Typography variant="caption" sx={{ color: K.green, fontWeight: 700 }}>
                          Según filtros activos
                        </Typography>
                      </Stack>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: `1px solid ${K.border}`,
                    borderLeft: `4px solid ${K.green}`,
                    bgcolor: '#fff',
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 1.5,
                        bgcolor: alpha(K.green, 0.2),
                        color: '#2E7D32',
                        display: 'flex',
                      }}
                    >
                      <AutoAwesomeIcon />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        Tasa de lectura (leído / total)
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: K.navy, my: 0.5 }}>
                        {seguimientoKpis.openRatePct}%
                      </Typography>
                      <Chip
                        size="small"
                        label="OPTIMIZADO POR IA"
                        sx={{
                          height: 22,
                          fontSize: '0.65rem',
                          fontWeight: 800,
                          bgcolor: alpha(K.green, 0.2),
                          color: '#1B5E20',
                        }}
                      />
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: `1px solid ${K.border}`,
                    borderLeft: `4px solid ${K.orange}`,
                    bgcolor: '#fff',
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 1.5,
                        bgcolor: alpha(K.orange, 0.15),
                        color: '#E65100',
                        display: 'flex',
                      }}
                    >
                      <InsightsIcon />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        Tasa de éxito de entrega
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: K.navy, my: 0.5 }}>
                        {seguimientoKpis.successRatePct}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Entregado, leído o completada
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          )}

          <Box
            sx={{
              mb: 3,
              p: 2,
              borderRadius: 2,
              bgcolor: K.surface,
              border: `1px solid ${K.border}`,
            }}
          >
            <Stack
              direction="row"
              flexWrap="wrap"
              alignItems="center"
              justifyContent="space-between"
              useFlexGap
              spacing={2}
            >
              <Stack direction="row" flexWrap="wrap" useFlexGap spacing={2} alignItems="flex-end">
                <Box sx={{ minWidth: 200 }}>
                  <Typography sx={kobraFilterLabelSx}>Canal</Typography>
                  <FormControl size="small" fullWidth>
                    <Select
                      labelId="kobra-canal-label"
                      label="Todos los canales"
                      value={platformFilter}
                      onChange={(e) => setPlatformFilter(e.target.value)}
                      sx={{ borderRadius: 1.5, bgcolor: '#fff' }}
                    >
                      <MenuItem value="all">Todos los canales</MenuItem>
                      <MenuItem value="whatsapp">WhatsApp</MenuItem>
                      <MenuItem value="llamada">Llamada</MenuItem>
                      <MenuItem value="email">Email</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ minWidth: 200 }}>
                  <Typography sx={kobraFilterLabelSx}>Estado</Typography>
                  <FormControl size="small" fullWidth>
                    <Select
                      labelId="kobra-estado-label"
                      label="Estado de entrega"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      sx={{ borderRadius: 1.5, bgcolor: '#fff' }}
                    >
                      <MenuItem value="all">Todos</MenuItem>
                      <MenuItem value="pendiente">Pendiente</MenuItem>
                      <MenuItem value="enviado">Enviado</MenuItem>
                      <MenuItem value="entregado">Entregado</MenuItem>
                      <MenuItem value="leido">Leído</MenuItem>
                      <MenuItem value="fallido">Fallido</MenuItem>
                      <MenuItem value="completada">Completada</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ minWidth: 150 }}>
                  <Typography sx={kobraFilterLabelSx}>Fecha desde</Typography>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={fechaInicio}
                      format="DD/MM/YYYY"
                      onChange={(newValue) => setFechaInicio(newValue)}
                      slotProps={{
                        textField: {
                          size: 'small',
                          sx: { minWidth: 150, ...kobraDateFieldSx },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Box>
                <Box sx={{ minWidth: 150 }}>
                  <Typography sx={kobraFilterLabelSx}>Fecha hasta</Typography>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={fechaFin}
                      format="DD/MM/YYYY"
                      onChange={(newValue) => setFechaFin(newValue)}
                      slotProps={{
                        textField: {
                          size: 'small',
                          sx: { minWidth: 150, ...kobraDateFieldSx },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Box>
              </Stack>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={clearHistorialFilters}
                sx={{
                  fontWeight: 700,
                  color: K.blue,
                  textDecoration: 'none',
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                  border: 'none',
                  background: 'none',
                  font: 'inherit',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Limpiar filtros
              </Link>
            </Stack>
          </Box>
        </>
      )}

      {/* Promesas para hoy (banner colapsable) + filtros — Mensajes Clasificados */}
      {activeTab === 1 && (
        <>
          <Accordion
            defaultExpanded
            sx={{
              ...kobraShell,
              mb: 2,
              overflow: 'hidden',
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: K.navy }} />}
              sx={{
                px: 2,
                py: 1.75,
                bgcolor: alpha(K.orange, 0.08),
                borderBottom: `1px solid ${K.border}`,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 1.5,
                    bgcolor: alpha(K.orange, 0.2),
                    color: '#E65100',
                    display: 'flex',
                  }}
                >
                  <CalendarIcon sx={{ fontSize: 24 }} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: K.navy }}>
                  Promesas para hoy
                </Typography>
                <Chip
                  size="small"
                  label={
                    isLoadingPromesasHoy
                      ? '…'
                      : `${promesasHoyData?.total ?? (promesasHoyData?.mensajes?.length ?? 0)}`
                  }
                  sx={{
                    fontWeight: 800,
                    bgcolor: alpha(K.orange, 0.18),
                    color: '#E65100',
                    border: `1px solid ${alpha(K.orange, 0.35)}`,
                  }}
                />
                <Typography variant="caption" sx={{ width: '100%', pl: { xs: 0, sm: 5 }, color: alpha(K.navy, 0.55) }}>
                  Compromisos con fecha de pago hoy ({dayjs().format('DD/MM/YYYY')}) y cumplimiento pendiente.
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 2, pt: 2, bgcolor: K.surface }}>
              {isLoadingPromesasHoy ? (
                <Typography color="text.secondary" sx={{ py: 1 }}>
                  Cargando promesas…
                </Typography>
              ) : (promesasHoyData?.mensajes ?? []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No hay promesas de pago pendientes con fecha para hoy.
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  {(promesasHoyData?.mensajes ?? []).map((mensaje) => {
                    const detalles = mensaje.clasificacion?.detalles || {};
                    const cliente = mensaje.cliente;
                    const rawText = mensaje.mensaje_texto || '';
                    const snippet =
                      rawText.length > 200 ? `${rawText.slice(0, 200)}…` : rawText;
                    return (
                      <Grid item xs={12} key={`hoy-${mensaje.mensaje_id}-${mensaje.clasificacion?.id || 'x'}`}>
                        <Card
                          variant="outlined"
                          sx={{
                            ...kobraShell,
                            borderLeft: `4px solid ${K.orange}`,
                          }}
                        >
                          <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5, color: K.navy }}>
                              {cliente?.nombre || 'Cliente'}
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap">
                              <Chip
                                icon={<PhoneIcon sx={{ fontSize: '16px !important', color: K.blue }} />}
                                label={mensaje.transmisor || '—'}
                                size="small"
                                variant="outlined"
                                sx={{ borderColor: K.border, fontWeight: 600 }}
                              />
                              {mensaje.clasificacion?.fecha_promesa && (
                                <Chip
                                  icon={<CalendarIcon sx={{ fontSize: '16px !important', color: K.orange }} />}
                                  label={dayjs(mensaje.clasificacion.fecha_promesa).format('DD/MM/YYYY')}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    borderColor: alpha(K.orange, 0.45),
                                    bgcolor: alpha(K.orange, 0.06),
                                    fontWeight: 700,
                                  }}
                                />
                              )}
                            </Stack>
                            <Box
                              sx={{
                                mb: 2,
                                p: 1.5,
                                bgcolor: '#fff',
                                borderRadius: 1.5,
                                border: `1px solid ${K.border}`,
                                borderLeft: `4px solid ${K.orange}`,
                              }}
                            >
                              <Typography variant="body2" sx={{ fontStyle: 'italic', lineHeight: 1.5 }}>
                                "{snippet}"
                              </Typography>
                            </Box>
                            {(detalles.fecha_mencionada_texto || detalles.fecha_mencionada) && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                Dicho: {detalles.fecha_mencionada_texto || detalles.fecha_mencionada}
                              </Typography>
                            )}
                            {mensaje.clasificacion?.id && (
                              <PromesaCumplimientoSection
                                clasificacionId={mensaje.clasificacion.id}
                                cumplimiento={mensaje.clasificacion.cumplimiento}
                                updateMutation={updateCumplimientoMutation}
                              />
                            )}
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                              Fecha de creación: {dayjs(mensaje.created_at).format('DD/MM/YYYY HH:mm')}
                            </Typography>

                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </AccordionDetails>
          </Accordion>

        <Box
          sx={{
            mb: 3,
            p: 2,
            borderRadius: 2,
            bgcolor: K.surface,
            border: `1px solid ${K.border}`,
          }}
        >
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item size={4}>
              <Typography sx={kobraFilterLabelSx}>Buscar</Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Mensaje, teléfono o cliente…"
                value={searchTermClasificados}
                onChange={(e) => setSearchTermClasificados(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'rgba(18,24,38,0.38)' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ ...kobraFieldSx, '& .MuiOutlinedInput-root': { ...kobraFieldSx['& .MuiOutlinedInput-root'], bgcolor: '#fff' } }}
              />
            </Grid>
            <Grid item size={4}>
              <Typography sx={kobraFilterLabelSx}>Tipo de clasificación</Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={tipoClasificacionFilter}
                  onChange={(e) => setTipoClasificacionFilter(e.target.value)}
                  sx={{ borderRadius: 1.5, bgcolor: '#fff' }}
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="desconocimiento_origen">Desconocimiento de Origen</MenuItem>
                  <MenuItem value="promesa_pago">Promesa de Pago</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item size={6}>
              <Typography sx={kobraFilterLabelSx}>Fecha desde</Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={fechaInicioClasificados}
                  format="DD/MM/YYYY"
                  onChange={(newValue) => setFechaInicioClasificados(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      sx: { ...kobraDateFieldSx },
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item size={6}>
              <Typography sx={kobraFilterLabelSx}>Fecha hasta</Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={fechaFinClasificados}
                  format="DD/MM/YYYY"
                  onChange={(newValue) => setFechaFinClasificados(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      sx: { ...kobraDateFieldSx },
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item size={12}>
              <Typography variant="caption" sx={{ display: 'block', mb: 1.5, color: alpha(K.navy, 0.55) }}>
                Elige tipo y/o rango de fechas y pulsa Buscar. El listado no se carga hasta entonces.
              </Typography>
              <Button variant="contained" onClick={ejecutarBusquedaClasificados} sx={kobraBtnPrimary}>
                Buscar
              </Button>
            </Grid>
          </Grid>
        </Box>
        </>
      )}

      {/* Data Grid - Historial de Estrategias */}
      {activeTab === 0 && (
        <>
          {isLoading ? (
            <Card sx={kobraShell}>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 6 }}>
                <Typography sx={{ color: alpha(K.navy, 0.55) }}>Cargando...</Typography>
              </Box>
            </Card>
          ) : filteredSeguimiento.length === 0 ? (
            <EmptyState
              icon={TrackChangesIcon}
              title="No hay registros de seguimiento"
              description="Los registros de mensajes enviados aparecerán aquí cuando se ejecuten las estrategias."
            />
          ) : (
            <Card
              sx={{
                borderRadius: 2,
                boxShadow: '0 1px 4px rgba(18, 24, 38, 0.06)',
                border: `1px solid ${K.border}`,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column' }}>
                <Box
                  sx={{
                    mb: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: K.navy }}>
                    Registros de envío
                  </Typography>
                  <Chip
                    label={`${filteredSeguimiento.length} registro${filteredSeguimiento.length !== 1 ? 's' : ''}`}
                    size="small"
                    sx={{
                      bgcolor: alpha(K.blue, 0.1),
                      color: K.blue,
                      fontWeight: 700,
                      borderRadius: 1.5,
                    }}
                  />
                </Box>
                <Box sx={{ width: '100%' }}>
                  <DataGrid
                    rows={filteredSeguimiento}
                    columns={columns}
                    initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                    pageSizeOptions={[10, 25, 50]}
                    disableRowSelectionOnClick
                    autoHeight
                    sx={historialDataGridSx}
                    getRowId={(row) => row.id}
                  />
                </Box>
              </Box>
            </Card>
          )}
        </>
      )}

      {/* Cards - Mensajes Clasificados */}
      {activeTab === 1 && (
        <>
          {clasificadosFiltrosAplicados == null ? (
            <EmptyState
              icon={MessageIcon}
              title="Busca mensajes clasificados"
              description="Configura los filtros arriba (tipo y/o fechas) y pulsa Buscar para cargar los resultados."
            />
          ) : isLoadingClasificados ? (
            <Card sx={kobraShell}>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 6 }}>
                <Typography sx={{ color: alpha(K.navy, 0.55) }}>Cargando...</Typography>
              </Box>
            </Card>
          ) : filteredMensajesClasificados.length === 0 ? (
            <EmptyState
              icon={MessageIcon}
              title="No hay mensajes clasificados"
              description="No hay resultados para los filtros seleccionados. Prueba otro rango o tipo de clasificación."
            />
          ) : (
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box
                sx={{
                  mb: 2.5,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: K.navy }}>
                  Mensajes clasificados
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  {mensajesClasificadosData?.total != null && (
                    <Chip
                      label={`Total: ${mensajesClasificadosData.total}`}
                      size="small"
                      sx={{
                        bgcolor: alpha(K.blue, 0.1),
                        color: K.blue,
                        fontWeight: 700,
                        borderRadius: 1.5,
                      }}
                    />
                  )}
                  <Chip
                    label={`${filteredMensajesClasificados.length} mostrado${filteredMensajesClasificados.length !== 1 ? 's' : ''}`}
                    size="small"
                    sx={{
                      bgcolor: alpha(K.navy, 0.06),
                      color: K.navy,
                      fontWeight: 700,
                      border: `1px solid ${K.border}`,
                      borderRadius: 1.5,
                    }}
                  />
                </Box>
              </Box>

              {/* Cards Grid */}
              <Grid container spacing={3} sx={{ pb: 3 }}>
                {filteredMensajesClasificados.map((mensaje) => {
                  const tipo = mensaje.clasificacion?.tipo;
                  const isDesconocimiento = tipo === 'desconocimiento_origen';
                  const confianza = mensaje.clasificacion?.confianza || 0;
                  const detalles = mensaje.clasificacion?.detalles || {};
                  const cliente = mensaje.cliente;
                  const fecha = mensaje.fecha ? dayjs(mensaje.fecha) : null;
                  const idPrestamoClasif = mensaje.clasificacion?.ultimo_pago?.loan_id;
                  
                  const ultimoPago = mensaje.clasificacion?.ultimo_pago;
                  const formatMoneyRD = (n) =>
                    typeof n === 'number' && !Number.isNaN(n)
                      ? `RD$ ${Number(n).toLocaleString('es-DO', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}`
                      : null;

                  const accent = isDesconocimiento ? K.orange : K.blue;
                  const accentSoft = isDesconocimiento ? alpha(K.orange, 0.12) : alpha(K.blue, 0.12);

                  return (
                    <Grid item xs={12} sm={4} md={4} size={4} key={`${mensaje.mensaje_id}-${mensaje.clasificacion?.id || 'no-clasif'}`}>
                      <Card
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          ...kobraShell,
                          borderLeft: `4px solid ${accent}`,
                          transition: 'box-shadow 0.2s, transform 0.2s',
                          '&:hover': {
                            boxShadow: `0 8px 24px ${alpha(K.navy, 0.08)}`,
                            transform: 'translateY(-2px)',
                          },
                        }}
                      >
                        <CardContent sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Box
                              sx={{
                                p: 1.5,
                                borderRadius: 1.5,
                                bgcolor: accentSoft,
                                color: accent,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              {isDesconocimiento ? (
                                <HelpOutlineIcon sx={{ fontSize: 28 }} />
                              ) : (
                                <PaymentIcon sx={{ fontSize: 28 }} />
                              )}
                            </Box>
                            <Chip
                              icon={isDesconocimiento ? <HelpOutlineIcon sx={{ color: `${accent} !important` }} /> : <PaymentIcon sx={{ color: `${accent} !important` }} />}
                              label={isDesconocimiento ? 'Desconocimiento' : 'Promesa Pago'}
                              size="small"
                              sx={{
                                fontWeight: 700,
                                bgcolor: accentSoft,
                                color: isDesconocimiento ? '#E65100' : K.blue,
                                border: `1px solid ${alpha(accent, 0.35)}`,
                              }}
                            />
                          </Box>

                          <Box
                            sx={{
                              mb: 2,
                              p: 2,
                              bgcolor: '#fff',
                              borderRadius: 1.5,
                              border: `1px solid ${K.border}`,
                              borderLeft: `4px solid ${accent}`,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontStyle: 'italic',
                                color: K.navy,
                                lineHeight: 1.6,
                              }}
                            >
                              "{mensaje.mensaje_texto || 'Sin mensaje'}"
                            </Typography>
                          </Box>

                          <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" sx={{ mb: 0.5, display: 'block', color: alpha(K.navy, 0.55) }}>
                              Confianza de clasificación
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box
                                sx={{
                                  flex: 1,
                                  height: 8,
                                  bgcolor: alpha(K.navy, 0.08),
                                  borderRadius: 1,
                                  overflow: 'hidden',
                                }}
                              >
                                <Box
                                  sx={{
                                    height: '100%',
                                    width: `${confianza * 100}%`,
                                    bgcolor:
                                      confianza >= 0.7 ? K.green : confianza >= 0.5 ? K.orange : '#E53935',
                                    transition: 'width 0.3s ease',
                                  }}
                                />
                              </Box>
                              <Chip
                                label={`${Math.round(confianza * 100)}%`}
                                size="small"
                                sx={{
                                  fontWeight: 700,
                                  minWidth: 50,
                                  bgcolor:
                                    confianza >= 0.7
                                      ? alpha(K.green, 0.15)
                                      : confianza >= 0.5
                                      ? alpha(K.orange, 0.15)
                                      : alpha('#E53935', 0.12),
                                  color: confianza >= 0.7 ? '#2E7D32' : confianza >= 0.5 ? '#E65100' : '#C62828',
                                }}
                              />
                            </Box>
                          </Box>

                          {!isDesconocimiento && (
                            <Box
                              sx={{
                                mb: 2,
                                p: 1.5,
                                borderRadius: 1.5,
                                bgcolor: K.surface,
                                border: `1px solid ${K.border}`,
                                borderLeft: `3px solid ${K.blue}`,
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ mb: 0.75, display: 'flex', alignItems: 'center', gap: 0.5 }}
                              >
                                <PaymentIcon sx={{ fontSize: 14 }} />
                                Último pago registrado
                              </Typography>
                              {!idPrestamoClasif ? (
                                <Typography variant="body2" color="text.secondary">
                                  Sin préstamo vinculado a esta clasificación.
                                </Typography>
                              ) : !ultimoPago ||
                                (ultimoPago.fecha == null &&
                                  (ultimoPago.monto_ultimo_pago == null || ultimoPago.monto_ultimo_pago === undefined)) ? (
                                <Typography variant="body2" color="text.secondary">
                                  Aún no hay pagos registrados en el préstamo.
                                </Typography>
                              ) : (
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                  {[
                                    ultimoPago.monto_ultimo_pago != null && ultimoPago.monto_ultimo_pago !== undefined
                                      ? formatMoneyRD(Number(ultimoPago.monto_ultimo_pago))
                                      : null,
                                    ultimoPago.fecha_ultimo_pago
                                      ? dayjs(ultimoPago.fecha_ultimo_pago).format('DD/MM/YYYY')
                                      : null,
                                  ]
                                    .filter(Boolean)
                                    .join(' · ') || '—'}
                                </Typography>
                              )}
                            </Box>
                          )}

                          {!isDesconocimiento && mensaje.clasificacion?.id && (
                            <PromesaCumplimientoSection
                              clasificacionId={mensaje.clasificacion.id}
                              cumplimiento={mensaje.clasificacion.cumplimiento}
                              updateMutation={updateCumplimientoMutation}
                            />
                          )}

                          {/* Details */}
                          {detalles.razon && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <InfoIcon sx={{ fontSize: 14 }} />
                                Razón
                              </Typography>
                              <Typography variant="body2" sx={{ pl: 2 }}>
                                {detalles.razon}
                              </Typography>
                            </Box>
                          )}

                          {(detalles.fecha_mencionada || detalles.fecha_mencionada_texto) && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CalendarIcon sx={{ fontSize: 14 }} />
                                Fecha Mencionada
                              </Typography>
                              <Typography variant="body2" sx={{ pl: 2, fontWeight: 600, color: K.blue }}>
                                {detalles.fecha_mencionada_texto || detalles.fecha_mencionada }{' '}({detalles.fecha_promesa_calendario ? detalles.fecha_promesa_calendario : 'fecha no especificada'})
                              </Typography>
                            </Box>
                          )}

                          <Box sx={{ my: 2, borderTop: `1px solid ${K.border}` }} />

                          {/* Client Info */}
                          <Box sx={{ mb: 2 }}>
                            {cliente ? (
                              <>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <PersonIcon sx={{ fontSize: 14 }} />
                                  Cliente
                                </Typography>
                                <Box sx={{ pl: 2 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {cliente.nombre || 'Sin nombre'}
                                  </Typography>
                                  {cliente.cedula && (
                                    <Typography variant="caption" color="text.secondary">
                                      Cédula: {cliente.cedula}
                                    </Typography>
                                  )}
                                </Box>
                              </>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                Cliente no identificado
                              </Typography>
                            )}
                          </Box>

                          {/* Contact Info */}
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <PhoneIcon sx={{ fontSize: 14 }} />
                              Teléfono
                            </Typography>
                            <Typography variant="body2" sx={{ pl: 2, fontWeight: 500 }}>
                              {mensaje.transmisor || 'N/A'}
                            </Typography>
                          </Box>

                          {/* Date */}
                          {fecha && (
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CalendarIcon sx={{ fontSize: 14 }} />
                                Fecha del Mensaje
                              </Typography>
                              <Typography variant="body2" sx={{ pl: 2 }}>
                                {fecha.format('DD/MM/YYYY HH:mm')}
                              </Typography>
                            </Box>
                          )}

                          {/* Message ID */}
                          <Box sx={{ mt: 'auto', pt: 2, borderTop: `1px solid ${K.border}` }}>
                            <Typography variant="caption" sx={{ color: alpha(K.navy, 0.45) }}>
                              ID Mensaje: {mensaje.mensaje_id}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}
        </>
      )}

      {/* Préstamos */}
      {activeTab === 2 && (
        <>
          {isLoading && isLoadingMensajesPrestamos && prestamosExpedienteData.length === 0 ? (
            <Card sx={kobraShell}>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 6 }}>
                <Typography sx={{ color: alpha(K.navy, 0.55) }}>Cargando resumen de préstamos...</Typography>
              </Box>
            </Card>
          ) : prestamosExpedienteData.length === 0 ? (
            <EmptyState
              icon={AccountBalanceIcon}
              title="No hay datos de préstamos"
              description="No se encontraron datos suficientes para construir el resumen por préstamo."
            />
          ) : (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 2.5,
                mb: 3,
                bgcolor: kobraPageBg,
                borderRadius: 2,
                p: { xs: 2, md: 3 },
              }}
            >
              <Card sx={kobraShell}>
                <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      letterSpacing: '0.06em',
                      color: alpha(K.navy, 0.45),
                      mb: 2,
                      fontWeight: 600,
                    }}
                  >
                    Seguimiento · Expedientes de préstamo
                  </Typography>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 800,
                          mb: 0.5,
                          fontSize: '1.25rem',
                          color: K.navy,
                          letterSpacing: '-0.02em',
                        }}
                      >
                        Expedientes de préstamo
                      </Typography>
                      <Typography variant="body2" sx={{ color: alpha(K.navy, 0.55), maxWidth: 560 }}>
                        Busca por ID o cliente y abre el expediente de cobranza completo.
                      </Typography>
                    </Box>
                    <Chip
                      label={`${prestamosExpedienteData.length} préstamo${prestamosExpedienteData.length !== 1 ? 's' : ''}`}
                      sx={{
                        fontWeight: 700,
                        alignSelf: 'flex-start',
                        bgcolor: alpha(K.blue, 0.1),
                        color: K.blue,
                        borderRadius: 1.5,
                        height: 32,
                        '& .MuiChip-label': { fontSize: '0.8125rem' },
                      }}
                    />
                  </Stack>
                  <Autocomplete
                    sx={{ mt: 3, ...kobraFieldSx }}
                    options={prestamosExpedienteData}
                    value={selectedPrestamo}
                    onChange={(_, value) => setSelectedPrestamoId(value?.id || '')}
                    getOptionLabel={(option) =>
                      `${option.id} · ${option.cliente?.nombre || 'Cliente no identificado'} · ${option.cliente?.cedula || '—'}`
                    }
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Buscar y seleccionar préstamo..."
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <SearchIcon sx={{ color: 'rgba(18,24,38,0.38)' }} />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </CardContent>
              </Card>

              {!selectedPrestamo ? (
                <Box sx={{ py: 1 }}>
                  <EmptyState
                    icon={SearchIcon}
                    title="Selecciona un expediente"
                    description="Elige un préstamo en el buscador para ver cliente, finanzas, historial de gestiones y notas."
                  />
                </Box>
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    {(() => {
                      const formatMoneyRD = (n) =>
                        typeof n === 'number'
                          ? `RD$ ${Number(n).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                          : '—';
                      const notasPrestamoLista = selectedPrestamo
                        ? notasPrestamo[selectedPrestamo.id] || []
                        : [];
                      const notasMostradas = notasPrestamoLista;
                      const parseTimelineDate = (value) => {
                        const d = dayjs(value);
                        return d.isValid() ? d : null;
                      };
                      const timelineEventos = [
                        ...selectedPrestamo.contactos.map((c) => ({
                          tipo: 'contacto',
                          fecha: c?.fecha || '—',
                          titulo: c?.canal || 'Contacto',
                          descripcion: c?.resultado || 'Gestión registrada',
                        })),
                        ...selectedPrestamo.respuestasDetalle.map((r) => ({
                          tipo: 'respuesta',
                          fecha: r?.fecha || '—',
                          titulo: 'Respuesta del cliente',
                          descripcion: r?.texto || 'Respuesta registrada',
                        })),
                        ...selectedPrestamo.promesas.map((p) => ({
                          tipo: 'promesa',
                          fecha: p?.fecha || '—',
                          titulo: `Promesa ${p?.estado ? `(${p.estado})` : ''}`.trim(),
                          descripcion: p?.compromiso || 'Compromiso registrado',
                        })),
                      ].sort((a, b) => {
                        const da = parseTimelineDate(a.fecha);
                        const db = parseTimelineDate(b.fecha);
                        if (!da && !db) return 0;
                        if (!da) return 1;
                        if (!db) return -1;
                        return db.valueOf() - da.valueOf();
                      });
                      const timelineEventosFiltrados =
                        timelineFilter === 'all'
                          ? timelineEventos
                          : timelineEventos.filter((evento) => evento.tipo === timelineFilter);
                      const score = selectedPrestamo.scoreRiesgo;
                      const riesgoChipSx =
                        score === 'Alto'
                          ? {
                              bgcolor: alpha('#E53935', 0.12),
                              color: '#C62828',
                              border: `1px solid ${alpha('#E53935', 0.35)}`,
                              fontWeight: 700,
                            }
                          : score === 'Medio'
                          ? {
                              bgcolor: alpha(K.orange, 0.12),
                              color: '#E65100',
                              border: `1px solid ${alpha(K.orange, 0.35)}`,
                              fontWeight: 700,
                            }
                          : {
                              bgcolor: alpha(K.green, 0.12),
                              color: '#2E7D32',
                              border: `1px solid ${alpha(K.green, 0.35)}`,
                              fontWeight: 700,
                            };
                      const kpiItems = [
                        {
                          label: 'Contactos',
                          value: selectedPrestamo.vecesContactadas,
                          sub: 'gestiones',
                          accent: K.blue,
                        },
                        {
                          label: 'Respuestas',
                          value: selectedPrestamo.respuestas,
                          sub: 'del cliente',
                          accent: alpha(K.blue, 0.85),
                        },
                        {
                          label: 'Promesas',
                          value: selectedPrestamo.promesasPago,
                          sub: 'de pago',
                          accent: K.green,
                        },
                        {
                          label: 'Último pago',
                          value: selectedPrestamo.ultimaVezPagado
                            ? dayjs(selectedPrestamo.ultimaVezPagado).format('DD/MM/YYYY')
                            : '—',
                          sub: 'fecha',
                          accent: K.navy,
                        },
                      ];
                      return (
                        <Card
                          sx={{
                            ...kobraShell,
                            overflow: 'hidden',
                            p: 0,
                            boxShadow: '0 4px 24px rgba(18, 24, 38, 0.07)',
                          }}
                        >
                          <Box
                            sx={{
                              px: { xs: 2.5, md: 3.5 },
                              py: 3,
                              bgcolor: '#fff',
                              borderBottom: `1px solid ${K.border}`,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                letterSpacing: '0.1em',
                                color: alpha(K.navy, 0.5),
                                mb: 1.5,
                                fontWeight: 700,
                                fontSize: '0.65rem',
                              }}
                            >
                              EXPEDIENTE DE COBRANZA
                            </Typography>
                            <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 1.25 }}>
                              <Typography
                                variant="h5"
                                sx={{
                                  fontWeight: 800,
                                  color: K.navy,
                                  letterSpacing: '-0.03em',
                                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                                }}
                              >
                                {selectedPrestamo.id}
                              </Typography>
                              <Chip
                                label={selectedPrestamo.estado || 'En gestión'}
                                size="small"
                                sx={{
                                  fontWeight: 700,
                                  height: 28,
                                  bgcolor: alpha(K.blue, 0.1),
                                  color: K.blue,
                                  border: `1px solid ${alpha(K.blue, 0.22)}`,
                                }}
                              />
                              {selectedPrestamo.scoreRiesgo && (
                                <Chip label={`Riesgo ${selectedPrestamo.scoreRiesgo}`} size="small" sx={riesgoChipSx} />
                              )}
                            </Stack>
                            <Typography variant="body2" sx={{ color: alpha(K.navy, 0.55), lineHeight: 1.5 }}>
                              {selectedPrestamo.cliente?.nombre || 'Cliente no identificado'}
                              {(selectedPrestamo.cliente?.cedula || selectedPrestamo.cliente?.telefono) &&
                                ` · ${[selectedPrestamo.cliente?.cedula, selectedPrestamo.cliente?.telefono].filter(Boolean).join(' · ')}`}
                            </Typography>
                          </Box>

                          <CardContent sx={{ p: { xs: 2, md: 3 }, bgcolor: kobraPageBg }}>
                            <Stack spacing={2.5}>
                              <Grid container spacing={2.5} alignItems="flex-start">
                                <Grid item xs={12} lg={7}>
                                  <Stack spacing={2.5}>
                              <Paper elevation={0} sx={{ ...kobraNestedCard, p: { xs: 2.5, md: 3 } }}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5} alignItems="flex-start">
                                  <Box
                                    sx={{
                                      p: 1.5,
                                      borderRadius: 2,
                                      bgcolor: alpha(K.blue, 0.1),
                                      color: K.blue,
                                      display: 'flex',
                                    }}
                                  >
                                    <AccountBalanceIcon sx={{ fontSize: 32 }} />
                                  </Box>
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={{ ...kobraFilterLabelSx, mb: 0.5 }}>Resumen financiero</Typography>
                                    <Typography
                                      variant="h4"
                                      sx={{
                                        fontWeight: 800,
                                        color: K.navy,
                                        letterSpacing: '-0.03em',
                                        lineHeight: 1.15,
                                        fontSize: { xs: '1.65rem', sm: '2rem' },
                                      }}
                                    >
                                      {formatMoneyRD(selectedPrestamo.saldoPendiente)}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: alpha(K.navy, 0.45) }}>
                                      Saldo pendiente
                                    </Typography>
                                  </Box>
                                </Stack>
                                <Divider sx={{ my: 2.5, borderColor: K.border }} />
                                <Grid container spacing={2}>
                                  <Grid item xs={12} sm={4}>
                                    <Typography sx={{ ...kobraFilterLabelSx, mb: 0.5 }}>Cuota mensual</Typography>
                                    <Typography sx={{ fontWeight: 700, color: K.navy }}>{formatMoneyRD(selectedPrestamo.cuotaMensual)}</Typography>
                                  </Grid>
                                  <Grid item xs={12} sm={4}>
                                    <Typography sx={{ ...kobraFilterLabelSx, mb: 0.5 }}>Días de atraso</Typography>
                                    <Typography sx={{ fontWeight: 700, color: K.navy }}>{selectedPrestamo.diasAtraso ?? '—'}</Typography>
                                  </Grid>
                                  <Grid item xs={12} sm={4}>
                                    <Typography sx={{ ...kobraFilterLabelSx, mb: 0.5 }}>Último pago</Typography>
                                    <Typography sx={{ fontWeight: 700, color: K.navy }}>
                                      {selectedPrestamo.ultimaVezPagado ? dayjs(selectedPrestamo.ultimaVezPagado).format('DD/MM/YYYY') : '—'}
                                    </Typography>
                                  </Grid>
                                </Grid>
                              </Paper>

                              <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                  <Card variant="outlined" sx={{ ...kobraNestedCard, height: '100%' }}>
                                    <CardContent sx={{ p: 3 }}>
                                      <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 2 }}>
                                        <Avatar
                                          sx={{
                                            width: 52,
                                            height: 52,
                                            bgcolor: alpha(K.blue, 0.14),
                                            color: K.blue,
                                            fontWeight: 800,
                                            fontSize: '1.05rem',
                                          }}
                                        >
                                          {kobraInitials(selectedPrestamo.cliente?.nombre)}
                                        </Avatar>
                                        <Box>
                                          <Typography sx={{ ...kobraFilterLabelSx, mb: 0.35 }}>Cliente</Typography>
                                          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: K.navy, lineHeight: 1.3 }}>
                                            {selectedPrestamo.cliente?.nombre || '—'}
                                          </Typography>
                                        </Box>
                                      </Stack>
                                      <Divider sx={{ borderColor: K.border, mb: 2 }} />
                                      <Stack spacing={1.75}>
                                        {[
                                          ['Cédula', selectedPrestamo.cliente?.cedula],
                                          ['Teléfono', selectedPrestamo.cliente?.telefono],
                                          ['Correo', selectedPrestamo.cliente?.correo],
                                          ['Dirección', selectedPrestamo.cliente?.direccion],
                                        ].map(([lab, val]) => (
                                          <Box key={lab}>
                                            <Typography sx={{ ...kobraFilterLabelSx, mb: 0.35 }}>{lab}</Typography>
                                            <Typography sx={{ fontWeight: 600, color: K.navy }}>{val || '—'}</Typography>
                                          </Box>
                                        ))}
                                      </Stack>
                                    </CardContent>
                                  </Card>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <Card variant="outlined" sx={{ ...kobraNestedCard, height: '100%' }}>
                                    <CardContent sx={{ p: 3 }}>
                                      <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 2 }}>
                                        <Avatar
                                          sx={{
                                            width: 52,
                                            height: 52,
                                            bgcolor: alpha(K.navy, 0.1),
                                            color: K.navy,
                                            fontWeight: 800,
                                            fontSize: '1.05rem',
                                          }}
                                        >
                                          {kobraInitials(selectedPrestamo.garante?.nombre)}
                                        </Avatar>
                                        <Box>
                                          <Typography sx={{ ...kobraFilterLabelSx, mb: 0.35 }}>Garante</Typography>
                                          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: K.navy, lineHeight: 1.3 }}>
                                            {selectedPrestamo.garante?.nombre || '—'}
                                          </Typography>
                                        </Box>
                                      </Stack>
                                      <Divider sx={{ borderColor: K.border, mb: 2 }} />
                                      <Stack spacing={1.75}>
                                        {[
                                          ['Cédula', selectedPrestamo.garante?.cedula],
                                          ['Teléfono', selectedPrestamo.garante?.telefono],
                                          ['Parentesco', selectedPrestamo.garante?.parentesco],
                                          ['Dirección', selectedPrestamo.garante?.direccion],
                                        ].map(([lab, val]) => (
                                          <Box key={lab}>
                                            <Typography sx={{ ...kobraFilterLabelSx, mb: 0.35 }}>{lab}</Typography>
                                            <Typography sx={{ fontWeight: 600, color: K.navy }}>{val || '—'}</Typography>
                                          </Box>
                                        ))}
                                      </Stack>
                                    </CardContent>
                                  </Card>
                                </Grid>
                              </Grid>

                              <Grid container spacing={2}>
                                {kpiItems.map((kpi) => (
                                  <Grid item xs={6} md={3} key={kpi.label}>
                                    <Paper
                                      elevation={0}
                                      sx={{
                                        ...kobraNestedCard,
                                        p: 2,
                                        height: '100%',
                                        borderTop: `3px solid ${kpi.accent}`,
                                      }}
                                    >
                                      <Typography sx={{ ...kobraFilterLabelSx, mb: 0.75, fontSize: '0.65rem' }}>{kpi.label}</Typography>
                                      <Typography variant="h6" sx={{ fontWeight: 800, color: K.navy, lineHeight: 1.2 }}>
                                        {kpi.value}
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: alpha(K.navy, 0.45) }}>
                                        {kpi.sub}
                                      </Typography>
                                    </Paper>
                                  </Grid>
                                ))}
                              </Grid>
                                  </Stack>
                                </Grid>

                                <Grid item xs={12} lg={5}>
                              <Paper
                                elevation={0}
                                sx={{
                                  ...kobraNestedCard,
                                  p: { xs: 2, md: 2.5 },
                                  height: { lg: '100%' },
                                  position: { lg: 'sticky' },
                                  top: { lg: 16 },
                                  maxHeight: { lg: 'calc(100vh - 120px)' },
                                  overflow: 'auto',
                                }}
                              >
                                <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ mb: 2 }}>
                                  <InsightsIcon sx={{ color: K.blue, mt: 0.25 }} />
                                  <Box>
                                    <Typography sx={{ fontWeight: 800, color: K.navy, fontSize: '1.05rem' }}>
                                      Historial de gestiones
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: alpha(K.navy, 0.55), mt: 0.25 }}>
                                      Contactos, respuestas y promesas en orden cronológico.
                                    </Typography>
                                  </Box>
                                </Stack>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                                  {[
                                    { id: 'all', label: 'Todos' },
                                    { id: 'contacto', label: 'Contactos' },
                                    { id: 'respuesta', label: 'Respuestas' },
                                    { id: 'promesa', label: 'Promesas' },
                                  ].map((f) => {
                                    const active = timelineFilter === f.id;
                                    const filled =
                                      f.id === 'promesa'
                                        ? { bg: K.green, fg: K.navy }
                                        : f.id === 'respuesta'
                                        ? { bg: alpha(K.blue, 0.9), fg: '#fff' }
                                        : { bg: K.blue, fg: '#fff' };
                                    return (
                                      <Chip
                                        key={f.id}
                                        label={f.label}
                                        size="small"
                                        onClick={() => setTimelineFilter(f.id)}
                                        sx={{
                                          fontWeight: 700,
                                          cursor: 'pointer',
                                          ...(active
                                            ? { bgcolor: filled.bg, color: filled.fg, border: 'none' }
                                            : {
                                                borderColor: K.border,
                                                color: K.navy,
                                                bgcolor: '#fff',
                                              }),
                                        }}
                                      />
                                    );
                                  })}
                                </Stack>
                                {timelineEventosFiltrados.length === 0 ? (
                                  <Typography variant="body2" sx={{ color: alpha(K.navy, 0.45), py: 2 }}>
                                    No hay eventos para el filtro seleccionado.
                                  </Typography>
                                ) : (
                                  <Box sx={{ position: 'relative', pl: 0.5 }}>
                                    <Box
                                      aria-hidden
                                      sx={{
                                        display: timelineEventosFiltrados.length < 2 ? 'none' : 'block',
                                        position: 'absolute',
                                        left: 15,
                                        top: 20,
                                        bottom: 20,
                                        width: 2,
                                        bgcolor: alpha(K.navy, 0.1),
                                        borderRadius: 1,
                                      }}
                                    />
                                    <Stack spacing={0}>
                                      {timelineEventosFiltrados.map((evento, idx) => {
                                        const IconCmp =
                                          evento.tipo === 'promesa'
                                            ? ScheduleIcon
                                            : evento.tipo === 'respuesta'
                                            ? ForumIcon
                                            : PhoneIcon;
                                        const dot =
                                          evento.tipo === 'promesa'
                                            ? K.green
                                            : evento.tipo === 'respuesta'
                                            ? K.blue
                                            : alpha(K.navy, 0.55);
                                        const kindLabel =
                                          evento.tipo === 'promesa'
                                            ? 'Promesa'
                                            : evento.tipo === 'respuesta'
                                            ? 'Respuesta'
                                            : 'Contacto';
                                        return (
                                          <Box
                                            key={`${evento.tipo}-${evento.fecha}-${idx}`}
                                            sx={{ display: 'flex', gap: 2, pb: 2.5, position: 'relative' }}
                                          >
                                            <Box
                                              sx={{
                                                width: 32,
                                                flexShrink: 0,
                                                display: 'flex',
                                                justifyContent: 'center',
                                                pt: 0.25,
                                              }}
                                            >
                                              <Box
                                                sx={{
                                                  width: 32,
                                                  height: 32,
                                                  borderRadius: '50%',
                                                  bgcolor: alpha(dot, 0.12),
                                                  color: dot,
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  border: '2px solid #fff',
                                                  boxShadow: `0 0 0 1px ${K.border}`,
                                                  zIndex: 1,
                                                }}
                                              >
                                                <IconCmp sx={{ fontSize: 17 }} />
                                              </Box>
                                            </Box>
                                            <Paper
                                              elevation={0}
                                              sx={{
                                                flex: 1,
                                                p: 2,
                                                borderRadius: 2,
                                                border: `1px solid ${K.border}`,
                                                bgcolor: '#fff',
                                              }}
                                            >
                                              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ mb: 0.75 }}>
                                                <Chip
                                                  label={kindLabel}
                                                  size="small"
                                                  sx={{
                                                    height: 22,
                                                    fontWeight: 700,
                                                    fontSize: '0.7rem',
                                                    bgcolor: alpha(dot, 0.12),
                                                    color: dot,
                                                    border: 'none',
                                                  }}
                                                />
                                                <Typography variant="caption" sx={{ color: alpha(K.navy, 0.45) }}>
                                                  {evento.fecha}
                                                </Typography>
                                              </Stack>
                                              <Typography sx={{ fontWeight: 700, color: K.navy, mb: 0.5 }}>{evento.titulo}</Typography>
                                              <Typography variant="body2" sx={{ color: alpha(K.navy, 0.55), lineHeight: 1.5 }}>
                                                {evento.descripcion}
                                              </Typography>
                                            </Paper>
                                          </Box>
                                        );
                                      })}
                                    </Stack>
                                  </Box>
                                )}
                              </Paper>
                                </Grid>
                              </Grid>

                              <Paper elevation={0} sx={{ ...kobraNestedCard, p: { xs: 2, md: 2.5 } }}>
                                <Typography sx={{ fontWeight: 800, color: K.navy, fontSize: '1.05rem', mb: 2 }}>
                                  Notas de gestión
                                </Typography>
                                <TextField
                                  fullWidth
                                  multiline
                                  minRows={3}
                                  placeholder="Escribe una nota de seguimiento para este préstamo..."
                                  value={notaPrestamoInput}
                                  onChange={(e) => setNotaPrestamoInput(e.target.value)}
                                  sx={{
                                    mb: 1.5,
                                    ...kobraFieldSx,
                                    '& .MuiOutlinedInput-root': { bgcolor: '#fff' },
                                    '& .MuiInputBase-inputMultiline': {
                                      overflowWrap: 'anywhere',
                                      wordBreak: 'break-word',
                                    },
                                  }}
                                />
                                <Button
                                  variant="contained"
                                  sx={{ ...kobraBtnPrimary, py: 1, px: 2.5 }}
                                  onClick={() => {
                                    if (!selectedPrestamo?.id) return;
                                    const value = (notaPrestamoInput || '').trim();
                                    if (!value) {
                                      showToast('Escribe una nota antes de guardar', 'warning');
                                      return;
                                    }
                                    setNotasPrestamo((prev) => ({
                                      ...prev,
                                      [selectedPrestamo.id]: [
                                        ...(prev[selectedPrestamo.id] || []),
                                        {
                                          texto: value,
                                          fecha: dayjs().toISOString(),
                                        },
                                      ],
                                    }));
                                    setNotaPrestamoInput('');
                                    showToast('Nota guardada correctamente', 'success');
                                  }}
                                >
                                  Guardar nota
                                </Button>
                                {notasMostradas.length === 0 ? (
                                  <Typography variant="body2" sx={{ color: alpha(K.navy, 0.45), mt: 2 }}>
                                    Sin notas registradas.
                                  </Typography>
                                ) : (
                                  <Stack spacing={1.25} sx={{ mt: 2 }}>
                                    {notasMostradas
                                      .slice()
                                      .reverse()
                                      .map((nota, idx) => (
                                        <Box
                                          key={`${nota.fecha || 'base'}-${idx}`}
                                          sx={{
                                            p: 1.5,
                                            border: `1px solid ${K.border}`,
                                            borderRadius: 2,
                                            bgcolor: '#fff',
                                          }}
                                        >
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              fontWeight: 600,
                                              color: K.navy,
                                              whiteSpace: 'pre-wrap',
                                              overflowWrap: 'anywhere',
                                              wordBreak: 'break-word',
                                            }}
                                          >
                                            {nota.texto}
                                          </Typography>
                                          <Typography variant="caption" sx={{ color: alpha(K.navy, 0.45), display: 'block', mt: 0.75 }}>
                                            {dayjs(nota.fecha).format('DD/MM/YYYY HH:mm')}
                                          </Typography>
                                          <Button
                                            size="small"
                                            sx={{ mt: 0.5, color: '#C62828', fontWeight: 600, textTransform: 'none' }}
                                            onClick={() => {
                                              if (!selectedPrestamo?.id) return;
                                              const confirmed = window.confirm('¿Deseas eliminar esta nota?');
                                              if (!confirmed) return;
                                              setNotasPrestamo((prev) => ({
                                                ...prev,
                                                [selectedPrestamo.id]: (prev[selectedPrestamo.id] || []).filter(
                                                  (item) => !(item.fecha === nota.fecha && item.texto === nota.texto)
                                                ),
                                              }));
                                              showToast('Nota eliminada', 'success');
                                            }}
                                          >
                                            Eliminar
                                          </Button>
                                        </Box>
                                      ))}
                                  </Stack>
                                )}
                              </Paper>
                            </Stack>
                          </CardContent>
                        </Card>
                      );
                    })()}
                  </Grid>
                </Grid>
              )}
            </Box>
          )}
        </>
      )}

      {/* Métricas */}
      {activeTab === 3 && (() => {
        const loansAlertList = loansUnderAlertData?.loans ?? [];
        const loansAlertTotal = loansAlertList.length;
        const getSeverity = (cuotas) => {
          if (cuotas >= 4) return { label: 'Alta', color: 'error' };
          if (cuotas >= 2) return { label: 'Media', color: 'warning' };
          return { label: 'Baja', color: 'info' };
        };
        const formatMoney = (n) =>
          typeof n === 'number' ? `RD$ ${Number(n).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';
        const formatDate = (d) => (d ? dayjs(d).format('DD/MM/YYYY') : '—');
        const severityBorder = (c) => {
          if (c === 'error') return '#E53935';
          if (c === 'warning') return '#FB8C00';
          return K.blue;
        };
        const severityChipSx = (c) => {
          if (c === 'error') return { bgcolor: alpha('#E53935', 0.12), color: '#C62828', border: `1px solid ${alpha('#E53935', 0.35)}`, fontWeight: 700 };
          if (c === 'warning') return { bgcolor: alpha(K.orange, 0.12), color: '#E65100', border: `1px solid ${alpha(K.orange, 0.35)}`, fontWeight: 700 };
          return { bgcolor: alpha(K.blue, 0.1), color: K.blue, border: `1px solid ${alpha(K.blue, 0.3)}`, fontWeight: 700 };
        };
        return (
        <Box
          sx={{
            mb: 4,
            bgcolor: kobraPageBg,
            borderRadius: 2,
            p: { xs: 2, md: 3 },
          }}
        >
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              letterSpacing: '0.06em',
              color: alpha(K.navy, 0.45),
              mb: 1.5,
              fontWeight: 600,
            }}
          >
            Seguimiento · Métricas
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 800, color: K.navy, letterSpacing: '-0.02em', mb: 0.5 }}>
            Métricas de cobranza
          </Typography>
          <Typography variant="body2" sx={{ color: alpha(K.navy, 0.55), mb: 3, maxWidth: 720 }}>
            Préstamos en alerta por mora e impacto de cobranzas por período. Prioriza seguimiento según severidad.
          </Typography>

          <Grid container spacing={2.5} alignItems="flex-start">
            <Grid item xs={12} lg={7}>
              <Accordion
                expanded={loansAlertExpanded}
                onChange={() => setLoansAlertExpanded((prev) => !prev)}
                TransitionProps={{ timeout: 50 }}
                sx={{
                  ...kobraShell,
                  overflow: 'hidden',
                  boxShadow: '0 4px 24px rgba(18, 24, 38, 0.07)',
                  borderColor: loansAlertTotal > 0 ? alpha(K.orange, 0.35) : K.border,
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: K.navy }} />}
                  sx={{
                    px: { xs: 2, md: 2.5 },
                    py: 2,
                    bgcolor: '#fff',
                    borderBottom: `1px solid ${K.border}`,
                    '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1.5 },
                  }}
                >
                  <Box
                    sx={{
                      p: 1.25,
                      borderRadius: 2,
                      bgcolor: loansAlertTotal > 0 ? alpha(K.orange, 0.12) : alpha(K.navy, 0.06),
                      color: loansAlertTotal > 0 ? '#E65100' : alpha(K.navy, 0.45),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <WarningIcon sx={{ fontSize: 24 }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 800, color: K.navy, fontSize: '1.05rem' }}>
                      Préstamos en alerta
                    </Typography>
                    <Typography variant="body2" sx={{ color: alpha(K.navy, 0.55), mt: 0.25 }}>
                      Cuotas vencidas que requieren seguimiento
                    </Typography>
                  </Box>
                  {loansAlertTotal > 0 && (
                    <Chip
                      label={`${loansAlertTotal} préstamo${loansAlertTotal !== 1 ? 's' : ''}`}
                      size="small"
                      sx={{
                        fontWeight: 800,
                        bgcolor: alpha(K.orange, 0.15),
                        color: '#E65100',
                        border: `1px solid ${alpha(K.orange, 0.35)}`,
                      }}
                    />
                  )}
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0, bgcolor: kobraPageBg }}>
                  <Box sx={{ p: { xs: 2, md: 2.5 }, pt: { xs: 2, md: 2 } }}>
                    {isLoadingLoansAlert ? (
                      <Box sx={{ py: 5, textAlign: 'center' }}>
                        <Typography variant="body2" sx={{ color: alpha(K.navy, 0.55) }}>
                          Cargando préstamos en alerta…
                        </Typography>
                      </Box>
                    ) : loansAlertTotal === 0 ? (
                      <Box
                        sx={{
                          py: 4,
                          px: 2,
                          textAlign: 'center',
                          borderRadius: 2,
                          border: `1px dashed ${K.border}`,
                          bgcolor: '#fff',
                        }}
                      >
                        <Typography variant="body2" sx={{ color: alpha(K.navy, 0.45) }}>
                          No hay préstamos con cuotas vencidas en este momento.
                        </Typography>
                      </Box>
                    ) : (
                      <Grid container spacing={2}>
                        {loansAlertList.map((loan) => {
                          const severity = getSeverity(loan.cuotas_atrasadas || 0);
                          return (
                            <Grid item xs={12} sm={6} key={loan.id}>
                              <Card
                                variant="outlined"
                                sx={{
                                  height: '100%',
                                  ...kobraShell,
                                  borderLeft: '4px solid',
                                  borderLeftColor: severityBorder(severity.color),
                                  transition: 'box-shadow 0.2s, transform 0.2s',
                                  '&:hover': {
                                    boxShadow: `0 8px 24px ${alpha(K.navy, 0.08)}`,
                                    transform: 'translateY(-2px)',
                                  },
                                }}
                              >
                                <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, gap: 1 }}>
                                    <Typography
                                      variant="subtitle2"
                                      sx={{
                                        fontWeight: 800,
                                        color: K.blue,
                                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                                      }}
                                    >
                                      {loan.loan_id || '—'}
                                    </Typography>
                                    <Chip label={severity.label} size="small" sx={severityChipSx(severity.color)} />
                                  </Box>
                                  <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 2 }}>
                                    <Box
                                      sx={{
                                        p: 0.75,
                                        borderRadius: 1.5,
                                        bgcolor: alpha(K.blue, 0.08),
                                        color: K.blue,
                                        display: 'flex',
                                      }}
                                    >
                                      <PersonIcon sx={{ fontSize: 20 }} />
                                    </Box>
                                    <Box sx={{ minWidth: 0 }}>
                                      <Typography sx={{ fontWeight: 700, color: K.navy, lineHeight: 1.35 }}>
                                        {loan.cliente_nombre || 'Sin nombre'}
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: alpha(K.navy, 0.5), display: 'block' }}>
                                        Cédula · {loan.cliente_identificacion || '—'}
                                      </Typography>
                                      {loan.cliente_telefono && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                          <PhoneIcon sx={{ fontSize: 14, color: alpha(K.navy, 0.45) }} />
                                          <Typography variant="caption" sx={{ color: alpha(K.navy, 0.55) }}>
                                            {loan.cliente_telefono}
                                          </Typography>
                                        </Box>
                                      )}
                                    </Box>
                                  </Stack>
                                  {loan.producto_financiero && (
                                    <Typography variant="caption" sx={{ color: alpha(K.navy, 0.45), display: 'block', mb: 1.5 }}>
                                      {loan.producto_financiero}
                                    </Typography>
                                  )}
                                  <Box
                                    sx={{
                                      p: 1.5,
                                      borderRadius: 2,
                                      bgcolor: alpha(K.orange, 0.06),
                                      border: `1px solid ${K.border}`,
                                    }}
                                  >
                                    <Typography sx={{ ...kobraFilterLabelSx, mb: 0.5, fontSize: '0.65rem' }}>Monto atrasado</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#E65100', letterSpacing: '-0.02em' }}>
                                      {formatMoney(loan.monto_atrasado)}
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.25 }}>
                                      <Chip
                                        size="small"
                                        label={`${loan.cuotas_atrasadas || 0} cuota(s)`}
                                        sx={{
                                          fontSize: '0.7rem',
                                          fontWeight: 600,
                                          borderColor: K.border,
                                          color: K.navy,
                                        }}
                                        variant="outlined"
                                      />
                                      {loan.dias_atraso != null && (
                                        <Chip
                                          size="small"
                                          label={`${loan.dias_atraso} días`}
                                          sx={{
                                            fontSize: '0.7rem',
                                            fontWeight: 600,
                                            borderColor: K.border,
                                            color: K.navy,
                                          }}
                                          variant="outlined"
                                        />
                                      )}
                                    </Box>
                                  </Box>
                                  <Box
                                    sx={{
                                      mt: 2,
                                      pt: 1.5,
                                      borderTop: `1px solid ${K.border}`,
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      flexWrap: 'wrap',
                                      gap: 2,
                                    }}
                                  >
                                    <Box>
                                      <Typography sx={{ ...kobraFilterLabelSx, mb: 0.35, fontSize: '0.65rem' }}>Último pago</Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 600, color: K.navy }}>
                                        {formatDate(loan.fecha_ultimo_pago)}
                                      </Typography>
                                    </Box>
                                    {loan.balance_pendiente != null && (
                                      <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                                        <Typography sx={{ ...kobraFilterLabelSx, mb: 0.35, fontSize: '0.65rem' }}>
                                          Balance pendiente
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: K.navy }}>
                                          {formatMoney(loan.monto_ultimo_pago)}
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                </CardContent>
                              </Card>
                            </Grid>
                          );
                        })}
                      </Grid>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Grid>

            <Grid item xs={12} lg={5}>
              <Box
                sx={{
                  position: { lg: 'sticky' },
                  top: { lg: 16 },
                  maxHeight: { lg: 'calc(100vh - 100px)' },
                  overflow: { lg: 'auto' },
                }}
              >
                <CollectionsImpactChart />
              </Box>
            </Grid>
          </Grid>
        </Box>
        );
      })()}

      {activeTab === 0 && (
        <Box
          sx={{
            mt: 3,
            p: { xs: 2.5, md: 3 },
            borderRadius: 2,
            bgcolor: K.blue,
            color: '#fff',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            boxShadow: `0 12px 40px ${alpha(K.blue, 0.35)}`,
          }}
        >
          <Box sx={{ maxWidth: 720 }}>
            <Chip
              label="SUGERENCIA DE KOBRA IA"
              size="small"
              sx={{
                mb: 1.5,
                bgcolor: alpha('#fff', 0.2),
                color: '#fff',
                fontWeight: 800,
                letterSpacing: '0.06em',
                fontSize: '0.65rem',
                height: 26,
                borderRadius: 1.5,
              }}
            />
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 1, letterSpacing: '-0.02em' }}>
              Maximiza tu recuperación hoy
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.95, lineHeight: 1.55 }}>
              Ajusta canales y mensajes según el historial de entrega. Las campañas bien segmentadas suelen mejorar
              tasas de respuesta y cobro.
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={() => navigate('/strategies')}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              px: 3,
              py: 1.25,
              borderRadius: 1.5,
              bgcolor: '#fff',
              color: K.blue,
              boxShadow: 'none',
              '&:hover': { bgcolor: alpha('#fff', 0.92), boxShadow: 'none' },
            }}
          >
            Crear campaña optimizada
          </Button>
        </Box>
      )}
    </Box>
  );
};

